import { useMemo, useState } from 'react';
import type { TrendResponse } from '@/lib/types';

interface Props {
  data: TrendResponse | undefined;
  loading: boolean;
  days: number;
  onDaysChange: (d: number) => void;
}

const WIDTH = 880;
const HEIGHT = 200;
const PADDING_LEFT = 36;
const PADDING_RIGHT = 12;
const PADDING_TOP = 16;
const PADDING_BOTTOM = 28;

const fmtDateShort = (iso: string): string =>
  new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });

const fmtDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

export function TrendChart({ data, loading, days, onDaysChange }: Props) {
  const items = data?.items ?? [];
  const licenses = data?.licenses_assigned ?? 0;
  const [hover, setHover] = useState<number | null>(null);

  const { points, ticks, yMax } = useMemo<{
    points: { x: number; y: number; raw: number; day: string }[];
    ticks: string[];
    yMax: number;
  }>(() => {
    if (items.length === 0) return { points: [], ticks: [], yMax: 0 };
    const ys = items.map((p) => p.active_users);
    const yMaxCalc = Math.max(1, ...ys, Math.ceil(licenses * 0.3));
    const innerW = WIDTH - PADDING_LEFT - PADDING_RIGHT;
    const innerH = HEIGHT - PADDING_TOP - PADDING_BOTTOM;
    const step = items.length > 1 ? innerW / (items.length - 1) : 0;
    const pts = items.map((p, i) => ({
      x: PADDING_LEFT + i * step,
      y: PADDING_TOP + innerH - (p.active_users / yMaxCalc) * innerH,
      raw: p.active_users,
      day: p.day,
    }));
    // 4 ticks de fecha repartidos
    const tickIdx = [0, Math.floor(items.length / 3), Math.floor((2 * items.length) / 3), items.length - 1];
    return { points: pts, ticks: tickIdx.map((i) => items[i]?.day ?? ''), yMax: yMaxCalc };
  }, [items, licenses]);

  const path = points.length
    ? `M ${points[0]!.x} ${points[0]!.y} ` +
      points.slice(1).map((p) => `L ${p.x} ${p.y}`).join(' ')
    : '';
  const area = points.length
    ? `${path} L ${points[points.length - 1]!.x} ${HEIGHT - PADDING_BOTTOM} L ${points[0]!.x} ${HEIGHT - PADDING_BOTTOM} Z`
    : '';
  const gridYs = [0, 0.25, 0.5, 0.75, 1].map((f) => ({
    y: PADDING_TOP + (1 - f) * (HEIGHT - PADDING_TOP - PADDING_BOTTOM),
    label: Math.round(yMax * f).toString(),
  }));

  const hovered = hover !== null ? points[hover] : null;

  return (
    <section className="panel">
      <header className="flex flex-wrap items-baseline justify-between gap-3 border-b border-ink-800 px-6 py-4">
        <div>
          <div className="label">Actividad diaria</div>
          <h2 className="mt-1 text-lg font-light text-ink-50">
            Usuarios distintos por día
          </h2>
        </div>
        <div className="flex items-center gap-1 text-xs">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => onDaysChange(d)}
              className={`border px-2 py-1 transition-colors ${
                days === d
                  ? 'border-ink-300 text-ink-100'
                  : 'border-ink-800 text-ink-500 hover:border-ink-700 hover:text-ink-300'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </header>
      <div className="relative px-6 py-5">
        {loading && items.length === 0 ? (
          <div className="grid h-40 place-items-center text-ink-500">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="grid h-40 place-items-center text-ink-500">Sin datos</div>
        ) : (
          <>
            <svg
              role="img"
              aria-label="Gráfico de actividad diaria"
              viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
              className="w-full"
              onMouseLeave={() => setHover(null)}
            >
              <defs>
                <linearGradient id="trend-fill" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="rgb(168 162 158)" stopOpacity="0.25" />
                  <stop offset="100%" stopColor="rgb(168 162 158)" stopOpacity="0" />
                </linearGradient>
              </defs>
              {/* Gridlines y labels Y */}
              {gridYs.map((g, i) => (
                <g key={i}>
                  <line
                    x1={PADDING_LEFT}
                    x2={WIDTH - PADDING_RIGHT}
                    y1={g.y}
                    y2={g.y}
                    stroke="rgb(41 37 36)"
                    strokeDasharray={i === gridYs.length - 1 ? '0' : '2 4'}
                    strokeWidth="1"
                  />
                  <text
                    x={PADDING_LEFT - 8}
                    y={g.y + 4}
                    fill="rgb(120 113 108)"
                    fontSize="10"
                    textAnchor="end"
                    fontFamily="ui-monospace, monospace"
                  >
                    {g.label}
                  </text>
                </g>
              ))}
              {/* Área */}
              <path d={area} fill="url(#trend-fill)" />
              {/* Línea */}
              <path
                d={path}
                fill="none"
                stroke="rgb(214 211 209)"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* Marker hover */}
              {hovered ? (
                <>
                  <line
                    x1={hovered.x}
                    x2={hovered.x}
                    y1={PADDING_TOP}
                    y2={HEIGHT - PADDING_BOTTOM}
                    stroke="rgb(87 83 78)"
                    strokeWidth="1"
                  />
                  <circle
                    cx={hovered.x}
                    cy={hovered.y}
                    r="3"
                    fill="rgb(214 211 209)"
                    stroke="rgb(12 10 9)"
                    strokeWidth="2"
                  />
                </>
              ) : null}
              {/* Hitboxes invisibles para hover */}
              {points.map((p, i) => (
                <rect
                  key={i}
                  x={p.x - 6}
                  y={PADDING_TOP}
                  width={12}
                  height={HEIGHT - PADDING_TOP - PADDING_BOTTOM}
                  fill="transparent"
                  onMouseEnter={() => setHover(i)}
                />
              ))}
              {/* X-axis ticks */}
              {ticks.map((t, i) => {
                const x =
                  PADDING_LEFT +
                  (i / (ticks.length - 1)) * (WIDTH - PADDING_LEFT - PADDING_RIGHT);
                return (
                  <text
                    key={i}
                    x={x}
                    y={HEIGHT - 10}
                    fill="rgb(120 113 108)"
                    fontSize="10"
                    textAnchor={i === 0 ? 'start' : i === ticks.length - 1 ? 'end' : 'middle'}
                    fontFamily="ui-monospace, monospace"
                  >
                    {fmtDateShort(t)}
                  </text>
                );
              })}
            </svg>
            {hovered ? (
              <div className="pointer-events-none absolute top-3 right-6 border border-ink-700 bg-ink-950/90 px-3 py-2 text-xs backdrop-blur-sm">
                <div className="tabular-nums text-ink-100">
                  {hovered.raw} usuarios activos
                </div>
                <div className="text-[0.65rem] text-ink-500">{fmtDate(hovered.day)}</div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}
